import { useEffect, useState, type ReactElement } from "react";

import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";

import { Field } from "@atlaskit/form";
import Select, { ValueType } from "@atlaskit/select";
import Textfield from "@atlaskit/textfield";
import { N200 } from "@atlaskit/theme/colors";

import { fontSize } from "@/components/signup/utils";
import { useUserContext } from "@/components/user-context";
import { ExistingSite } from "@/libraries/bxp-web-stargate-client/src";
import { wacToken } from "@/libraries/design/foundations";

import { useSiteNameContext } from "../../site-name-context";

export const SitePickerField = ({
    label: labelProp
}: {
    label?: ReactElement;
}) => {
    const [availableSites, setAvailableSites] = useState<ExistingSite[]>([]);

    const {
        sitePickerValueState: [sitePickerValue, setSitePickerValue],
        isSubmittingState: [isSubmitting],
        productData: { productKey }
    } = useSiteNameContext();

    const { profile } = useUserContext();

    useEffect(() => {
        if (profile.attributes?.existingSites) {
            setAvailableSites(
                profile.attributes?.existingSites.filter(
                    ({ products }) =>
                        !products.some(
                            (product) => product.productKey === productKey
                        )
                ) ?? []
            );
        }
    }, [productKey, profile.attributes?.existingSites]);

    useEffect(() => {
        if (!profile.requestInProgress && availableSites.length) {
            setSitePickerValue(availableSites[0]);
        }
    }, [availableSites, profile.requestInProgress, setSitePickerValue]);

    return availableSites.length === 1 ? (
        <div css={fieldWrapperCss}>
            <Field
                aria-required
                isRequired
                isDisabled
                name="site-picker-field"
                id="site-picker-field"
                label={labelProp ?? label}
                defaultValue={availableSites[0]?.url.replace("https://", "")}
            >
                {({ fieldProps }) => <Textfield {...fieldProps} />}
            </Field>
        </div>
    ) : (
        <div css={fieldWrapperCss}>
            <Field<ValueType<ExistingSite>>
                aria-required
                isRequired
                defaultValue={availableSites[0]}
                isDisabled={isSubmitting}
                id="site-picker-field"
                name="site-picker-field"
                label={labelProp ?? label}
            >
                {({ fieldProps: { id, ...rest } }) => (
                    <Select
                        {...rest}
                        inputId={id}
                        options={availableSites}
                        getOptionLabel={(option) =>
                            option.url.replace("https://", "")
                        }
                        getOptionValue={(option) => option.cloudId}
                        value={sitePickerValue}
                        onChange={(newValue) =>
                            setSitePickerValue(newValue ?? availableSites[0])
                        }
                        isLoading={profile.requestInProgress}
                        isSearchable={false}
                        styles={{
                            menu: (base) => ({
                                ...base,
                                fontSize: fontSize.md
                            }),
                            valueContainer: (base) => ({
                                ...base,
                                fontSize: fontSize.md,
                                paddingTop: "8px",
                                paddingRight: "6px",
                                paddingBottom: "8px",
                                paddingLeft: "6px"
                            }),
                            singleValue: (base) => ({
                                ...base,
                                margin: 0
                            })
                        }}
                    />
                )}
            </Field>
        </div>
    );
};

export const WorkspacePickerField = () => {
    return <SitePickerField label={workspaceLabel} />;
};

const fieldWrapperCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["div[class$='-singleValue']"]: {
        fontFamily: wacToken("font.charlie.text")
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["input"]: {
        fontSize: fontSize.md,
        fontFamily: wacToken("font.charlie.text"),
        paddingInlineStart: "12px !important"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["label span:nth-of-type(2)"]: {
        display: "none"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["div[class$='-MenuList']"]: {
        fontFamily: wacToken("font.charlie.text"),
        padding: "0 !important",
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ["> div"]: {
            paddingInline: 12,
            paddingBlock: 10
        }
    }
});

const label = (
    <span
        css={css({
            color: N200,
            fontWeight: 500
        })}
    >
        <FormattedMessage
            id="WacSignupForm.siteNameN2ELabel"
            defaultMessage="Your site"
        />
    </span>
);

const workspaceLabel = (
    <span
        css={css({
            color: N200,
            fontWeight: 500
        })}
    >
        <FormattedMessage
            id="BxpUiSignup.nameSite.inputWorkspaceNameLabel"
            defaultMessage="Workspace name"
        />
    </span>
);
